import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Subtle wind streak - short and faint
const WindStreak = ({ x, startY, side, delay }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.2) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 0.2;
    const easeOut = 1 - Math.pow(1 - progress, 2);

    // Move sideways gently
    meshRef.current.position.x = x + side * easeOut * 0.4;
    meshRef.current.position.y = startY + easeOut * 0.5;

    // Fade out quickly
    meshRef.current.material.opacity = 0.25 * (1 - easeOut);
    meshRef.current.scale.y = 0.5 * (1 - progress * 0.5);
  });

  return (
    <mesh ref={meshRef} position={[x, startY, 0.1]} visible={false}>
      <planeGeometry args={[0.08, 0.5]} />
      <meshBasicMaterial
        color={0xffffff}
        transparent
        opacity={0.25}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// Very subtle trail
const GlowTrail = ({ x, startY, endY }) => {
  const meshRef = useRef();
  const startTime = useRef(null);
  const trailHeight = Math.abs(startY - endY);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    if (elapsed > 0.15) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = elapsed / 0.15;

    // Quick shrink upward
    const shrink = 1 - progress;
    meshRef.current.scale.y = shrink;
    meshRef.current.position.y = endY + (trailHeight * shrink) / 2;

    // Fade out fast
    meshRef.current.material.opacity = 0.15 * (1 - progress);
  });

  return (
    <mesh ref={meshRef} position={[x, endY + trailHeight / 2, 0.02]} visible={false}>
      <planeGeometry args={[0.5, trailHeight]} />
      <meshBasicMaterial
        color={0x88aacc}
        transparent
        opacity={0.15}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

const SpectrumHardDropTrail = ({ startY, endY, columns, onComplete }) => {
  const startTime = useRef(Date.now());
  const trailHeight = Math.abs(startY - endY);

  // Generate just a few wind streaks
  const windStreaks = useMemo(() => {
    const streaks = [];
    columns.forEach((col) => {
      const x = col - 5 + 0.5;
      // Only 2 streaks per column - left and right at the bottom
      const y = endY + 1;
      streaks.push({ x: x - 0.3, y, side: -1, delay: 0 });
      streaks.push({ x: x + 0.3, y, side: 1, delay: 0 });
    });
    return streaks;
  }, [columns, endY]);

  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;

    if (elapsed > 0.25) {
      onComplete?.();
    }
  });

  return (
    <group>
      {/* Subtle glow trails */}
      {columns.map((col, i) => {
        const x = col - 5 + 0.5;
        return (
          <GlowTrail
            key={`glow-${i}`}
            x={x}
            startY={startY}
            endY={endY}
          />
        );
      })}

      {/* Minimal wind streaks */}
      {windStreaks.map((streak, i) => (
        <WindStreak
          key={`streak-${i}`}
          x={streak.x}
          startY={streak.y}
          side={streak.side}
          delay={streak.delay}
        />
      ))}
    </group>
  );
};

export default SpectrumHardDropTrail;
